import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import BookingContextPanel from './BookingContextPanel';
import AgentRunPanel from './AgentRunPanel';
import EmailInboxService from '../../../Services/EmailInboxService';
import { conversationAge } from '../../../Utils/conversationAge';

/**
 * THE RIGHT PANE: everything about this conversation that is not in it.
 *
 * Four things, in the order an agent asks for them: the booking it is about,
 * who the person is, what else they have written to us, and what the agent
 * looked up before drafting. Each of them is something a mail client cannot
 * answer, which is the whole reason this console is in the dashboard.
 *
 * THE BOOKING IS HERE RATHER THAN IN THE THREAD, and that is the change. It
 * used to sit above the messages, which meant it scrolled away the moment
 * somebody started reading — the facts an agent needs WHILE reading were the
 * first thing off the screen. A column holds.
 *
 * EVERY SECTION RESOLVES OR IS ABSENT. Plenty of conversations have no
 * booking, no other threads and no agent run; nothing resolving is the normal
 * case and renders nothing, never an error.
 */

/**
 * The other conversations this person has had with us.
 *
 * READ THROUGH THE ARCHIVE'S OWN LIST, by exact sender — there is no second
 * email service and there must not be. `fromEmail` is an equality on the From
 * address, which is right here: this is "the same person", not "somebody at
 * the same company", and the domain filter would pull in every colleague of a
 * partner's booking desk.
 *
 * IT IS THEIR MAIL, NOT THEIR THREADS. A conversation the office opened and
 * they never answered has no message FROM them in it, so it cannot appear
 * here. Worth knowing rather than worth hiding: the alternative is a
 * per-participant index the archive does not have.
 */
function OtherThreads({ address, exceptId, onSelect }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    if (!address || !address.includes('@')) {
      setThreads([]);
      return undefined;
    }
    setLoading(true);
    EmailInboxService.listConversations({ fromEmail: address, limit: 10 })
      .then((result) => {
        if (live) setThreads(result.conversations.filter((one) => one.id !== exceptId));
      })
      .catch(() => {
        if (live) setThreads([]);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => { live = false; };
  }, [address, exceptId]);

  if (loading) {
    return (
      <div className="cs-context__loading">
        <Spinner animation="border" size="sm" className="me-2" />
        Looking for their other mail
      </div>
    );
  }

  if (threads.length === 0) return null;

  return (
    <section className="cs-others">
      <div className="cs-context__title">
        <i className="fa fa-layer-group me-2" aria-hidden="true" />
        {`Their other conversations (${threads.length})`}
      </div>
      <ul className="cs-others__list">
        {threads.map((thread) => (
          <li key={thread.id}>
            <button
              type="button"
              className="cs-others__row"
              onClick={() => onSelect(thread.id)}
              data-testid="cs-other-thread"
            >
              <span className="cs-others__title">{thread.title || 'No subject'}</span>
              <span className="cs-others__age">
                {conversationAge(thread.last_message?.sent_at)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ContextPanel({
  conversation, reference, who, run, onSelectConversation,
}) {
  if (!conversation) return null;

  return (
    <div className="cs-context">
      <BookingContextPanel reference={reference} />

      <section className="cs-who">
        <div className="cs-context__title">
          <i className="fa fa-user me-2" aria-hidden="true" />
          Who this is
        </div>
        <div className="cs-who__address">{who || 'Unknown sender'}</div>
        <div className="cs-who__people">
          {(conversation.participants || []).join(', ')}
        </div>
      </section>

      <AgentRunPanel run={run} />

      <OtherThreads
        address={who}
        exceptId={conversation.id}
        onSelect={onSelectConversation}
      />
    </div>
  );
}

export default ContextPanel;
